<template>
  <div class="script-step">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>剧本拆解</span>
          <el-button type="primary" @click="generateScript" :loading="generating">
            <el-icon><MagicStick /></el-icon>
            AI拆解剧本
          </el-button>
        </div>
      </template>

      <el-empty v-if="!hasScript" description="暂无剧本数据">
        <p>点击"AI拆解剧本"按钮，将小说原文拆解为场景化剧本</p>
      </el-empty>

      <div v-else class="script-content">
        <el-timeline>
          <el-timeline-item
            v-for="(scene, index) in scenes"
            :key="index"
            :type="index === 0 ? 'primary' : ''"
            :hollow="index !== 0"
          >
            <el-card shadow="hover" class="scene-card">
              <template #header>
                <div class="scene-header">
                  <span class="scene-title">{{ scene.title || `场景 ${index + 1}` }}</span>
                  <div class="scene-actions">
                    <el-tag size="small" class="duration-tag">{{ scene.duration || 5 }}秒</el-tag>
                    <el-button type="primary" size="small" @click="editScene(scene, index)">
                      <el-icon><Edit /></el-icon>
                      修改
                    </el-button>
                    <el-button type="danger" size="small" @click="removeScene(index)">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
              </template>
              
              <p class="scene-description">{{ scene.description }}</p>

              <div class="scene-details">
                <div v-if="scene.dialogue && scene.dialogue.length > 0" class="dialogue-section">
                  <p><strong>台词：</strong></p>
                  <div v-for="(line, idx) in scene.dialogue" :key="idx" class="dialogue-line">
                    <span class="speaker">{{ line.speaker }}：</span>
                    <span class="content">{{ line.content }}</span>
                    <el-tag v-if="line.emotion" size="small" type="info" class="emotion-tag">{{ line.emotion }}</el-tag>
                  </div>
                </div>
                <p v-else><strong>台词：</strong>无</p>
                <p><strong>出镜角色：</strong>{{ scene.characters?.join(', ') || '无' }}</p>
                <p><strong>地点：</strong>{{ scene.location || '未指定' }}</p>
              </div>
            </el-card>
          </el-timeline-item>
        </el-timeline>

        <div class="actions">
          <el-button type="success" @click="confirmScript" size="large">
            <el-icon><Check /></el-icon>
            确认剧本并继续
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 场景编辑对话框 -->
    <el-dialog
      v-model="sceneDialog.visible"
      :title="sceneDialog.isEdit ? '编辑场景' : '添加场景'"
      width="800px"
    >
      <el-form :model="sceneDialog.form" label-position="top">
        <el-form-item label="场景标题">
            <el-input v-model="sceneDialog.form.title" placeholder="输入场景标题（如：初次相遇）" />
          </el-form-item>
          <el-form-item label="场景描述" required>
          <el-input
            v-model="sceneDialog.form.description"
            type="textarea"
            :rows="3"
            placeholder="描述这个场景的内容..."
          />
        </el-form-item>
        <el-form-item label="旁白">
          <el-input
            v-model="sceneDialog.form.narration"
            type="textarea"
            :rows="2"
            placeholder="输入旁白内容..."
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="地点">
              <el-input v-model="sceneDialog.form.location" placeholder="场景发生的地点..." />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="时长（秒）">
              <el-input-number v-model="sceneDialog.form.duration" :min="1" :max="60" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="出镜角色">
          <el-select
            v-model="sceneDialog.form.characters"
            multiple
            placeholder="选择出镜角色"
            style="width: 100%"
          >
            <el-option
              v-for="char in availableCharacters"
              :key="char.name"
              :label="char.name"
              :value="char.name"
            />
          </el-select>
        </el-form-item>

        <!-- 台词编辑 -->
        <el-divider>台词编辑</el-divider>
        <div v-for="(line, idx) in sceneDialog.form.dialogue" :key="idx" class="dialogue-edit-item">
          <el-row :gutter="10">
            <el-col :span="6">
              <el-select v-model="line.speaker" placeholder="说话人" style="width: 100%">
                <el-option
                  v-for="char in availableCharacters"
                  :key="char.name"
                  :label="char.name"
                  :value="char.name"
                />
              </el-select>
            </el-col>
            <el-col :span="14">
              <el-input v-model="line.content" placeholder="台词内容" />
            </el-col>
            <el-col :span="4">
              <el-button type="danger" size="small" @click="removeDialogueLine(idx)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </el-col>
          </el-row>
          <el-row :gutter="10" style="margin-top: 8px;">
            <el-col :span="6">
              <el-input v-model="line.emotion" placeholder="情绪（可选）" size="small" />
            </el-col>
          </el-row>
        </div>
        <el-button type="primary" plain @click="addDialogueLine" style="margin-top: 10px;">
          <el-icon><Plus /></el-icon>
          添加台词
        </el-button>
      </el-form>
      <template #footer>
        <el-button @click="sceneDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveScene">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { useProjectStore } from '../../stores/project'
import { generateScripts, getScripts, saveScripts, getSettings } from '../../api/project'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const projectStore = useProjectStore()
const scenes = ref([])
const availableCharacters = ref([])
const generating = ref(false)

// 场景编辑对话框
const sceneDialog = reactive({
  visible: false,
  isEdit: false,
  index: -1,
  form: {
    title: '',
    description: '',
    narration: '',
    characters: [],
    location: '',
    duration: 5,
    dialogue: []
  }
})

// 计算属性
const hasScript = computed(() => scenes.value.length > 0)

// 加载已有剧本和设定
onMounted(async () => {
  await loadScripts()
  await loadSettings()
})

const loadScripts = async () => {
  try {
    const response = await getScripts(props.projectId)
    if (response && response.length > 0) {
      scenes.value = response.map(script => ({
        title: script.scene_title || '',
        description: script.scene_description || '',
        narration: script.narration || '',
        dialogue: script.dialogue || [],
        characters: script.characters_present || [],
        location: script.location || '',
        props: script.props_present || [],
        duration: script.duration || 5,
        visual_style: script.image_prompt || ''
      }))
    }
  } catch (error) {
    console.error('Load scripts error:', error)
  }
}

const loadSettings = async () => {
  try {
    const settings = await getSettings(props.projectId)
    if (settings && settings.length > 0) {
      // 提取角色列表
      const characters = []
      settings.forEach(setting => {
        if (setting.setting_type === 'character') {
          const value = typeof setting.setting_value === 'string' 
            ? JSON.parse(setting.setting_value) 
            : setting.setting_value
          if (value && value.name) {
            characters.push(value)
          }
        }
      })
      availableCharacters.value = characters
    }
  } catch (error) {
    console.error('Load settings error:', error)
  }
}

const generateScript = async () => {
  generating.value = true
  try {
    // 获取小说原文
    const text = projectStore.project?.original_text
    if (!text) {
      ElMessage.warning('请先上传小说原文')
      return
    }

    // 获取设定数据
    const settings = await getSettings(props.projectId)
    if (!settings || settings.length === 0) {
      ElMessage.warning('请先提取或添加设定')
      return
    }

    // 构建设定数据
    const settingsData = {
      characters: [],
      locations: [],
      props: [],
      world: {}
    }

    settings.forEach(setting => {
      const value = typeof setting.setting_value === 'string' 
        ? JSON.parse(setting.setting_value) 
        : setting.setting_value

      switch (setting.setting_type) {
        case 'character':
          settingsData.characters.push(value)
          break
        case 'location':
          settingsData.locations.push(value)
          break
        case 'prop':
          settingsData.props.push(value)
          break
        case 'worldview':
          settingsData.world = { ...settingsData.world, ...value }
          break
      }
    })

    // 调用API生成剧本
    const response = await generateScripts(props.projectId, text, settingsData)

    if (response && response.scenes && response.scenes.length > 0) {
      scenes.value = response.scenes.map(scene => ({
        title: scene.title || '',
        description: scene.description || '',
        narration: scene.narration || '',
        dialogue: scene.dialogue || [],
        characters: scene.characters_present || scene.characters || [],
        location: scene.location || '',
        duration: scene.duration || 5,
        props: scene.props_present || [],
        visual_style: scene.visual_style || '',
        camera_angle: scene.camera_angle || ''
      }))
      ElMessage.success('剧本生成成功')
    } else {
      ElMessage.warning('未能生成剧本场景')
    }
  } catch (error) {
    console.error('Generate script error:', error)
    ElMessage.error('剧本生成失败')
  } finally {
    generating.value = false
  }
}

// 场景操作
const editScene = (row, index) => {
  sceneDialog.isEdit = true
  sceneDialog.index = index
  sceneDialog.form = {
    title: row.title || '',
    description: row.description || '',
    narration: row.narration || '',
    characters: [...(row.characters || [])],
    location: row.location || '',
    duration: row.duration || 5,
    dialogue: row.dialogue ? JSON.parse(JSON.stringify(row.dialogue)) : []
  }
  sceneDialog.visible = true
}

const removeScene = (index) => {
  scenes.value.splice(index, 1)
  ElMessage.success('场景已删除')
}

const addDialogueLine = () => {
  sceneDialog.form.dialogue.push({
    speaker: '',
    content: '',
    emotion: ''
  })
}

const removeDialogueLine = (idx) => {
  sceneDialog.form.dialogue.splice(idx, 1)
}

const saveScene = () => {
  if (!sceneDialog.form.description) {
    ElMessage.warning('请输入场景描述')
    return
  }
  
  if (sceneDialog.isEdit) {
    scenes.value[sceneDialog.index] = { ...sceneDialog.form }
  } else {
    scenes.value.push({ ...sceneDialog.form })
  }
  
  sceneDialog.visible = false
  ElMessage.success(sceneDialog.isEdit ? '场景修改成功' : '场景添加成功')
}

const confirmScript = async () => {
  if (scenes.value.length === 0) {
    ElMessage.warning('没有可保存的剧本')
    return
  }

  try {
    // 转换数据格式以匹配后端期望
    const scenesToSave = scenes.value.map((scene, index) => ({
      scene_id: `SCENE_${String(index + 1).padStart(3, '0')}`,
      scene_title: scene.title || `场景${index + 1}`,
      scene_description: scene.description || '',
      dialogue: scene.dialogue || [],
      characters_present: scene.characters || [],
      location: scene.location || '',
      props_present: scene.props || [],
      duration: scene.duration || 5,
      image_prompt: scene.visual_style || ''
    }))

    // 保存剧本到数据库
    await saveScripts(props.projectId, scenesToSave)
    
    // 更新项目状态
    await projectStore.updateProjectStatus('script_generated')
    
    // 重新加载剧本
    await loadScripts()

    ElMessage.success('剧本保存成功')
    emit('step-complete')
  } catch (error) {
    console.error('Save script error:', error)
    ElMessage.error('剧本保存失败')
  }
}
</script>

<style scoped lang="scss">
.script-step {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.scene-card {
  margin-bottom: 8px;
  
  .scene-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .scene-title {
      font-weight: 600;
    }
    
    .scene-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .duration-tag {
        margin-right: 8px;
      }
    }
  }
  
  .scene-description {
    margin-bottom: 12px;
    line-height: 1.6;
  }
  
  .scene-details {
    font-size: 14px;
    color: #606266;
    
    p {
      margin: 4px 0;
    }
  }
}

.actions {
  margin-top: 24px;
  text-align: center;
}

.dialogue-section {
  margin: 12px 0;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;

  .dialogue-line {
    margin: 8px 0;
    padding: 8px;
    background: white;
    border-radius: 4px;
    border-left: 3px solid #409eff;

    .speaker {
      font-weight: 600;
      color: #409eff;
    }

    .content {
      margin-left: 8px;
    }

    .emotion-tag {
      margin-left: 8px;
    }
  }
}

.dialogue-edit-item {
  padding: 12px;
  margin-bottom: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}
</style>
